import { getEnvValue } from './env.utils'

/**
 * Resolves the D2C auth domain for B2B self-serve hostnames.
 *
 * For feature preview branches, the D2C domain is derived from the B2B
 * subdomain pattern: <tenant>-<branch>-shop.preview-<env>.quince.com
 * → <branch>.preview-<env>.quince.com
 *
 * For all other hosts, falls back to the NEXT_PUBLIC_SITE_URL hostname.
 */

const SITE_URL = getEnvValue(process.env.NEXT_PUBLIC_SITE_URL, 'https://www.quince.com/', { required: false })

/**
 * Derives the D2C auth domain for feature-preview B2B deployments.
 *
 * B2B preview pattern: `<tenant>-<branch>-shop.preview-<env>.quince.com`
 * D2C preview result:  `<branch>.preview-<env>.quince.com`
 *
 * Extracts the branch name by stripping the `-shop` suffix, then treating
 * everything after the first hyphen as the branch.
 *
 * LIMITATION: Assumes tenant names are single-word (no hyphens). A hyphenated
 * tenant like `my-company` on branch `feat` produces `my-company-feat-shop`,
 * which would be parsed as tenant=`my`, branch=`company-feat` (incorrect).
 * If hyphenated tenants are needed, the hostname convention must change
 * (e.g. double-hyphen or underscore delimiter).
 *
 * Returns undefined when the host is not a preview domain.
 */
function getPreviewD2CDomain(host: string): string | undefined {
  if (!host.includes('preview-')) return undefined

  // Split into subdomain and the rest (e.g. "preview-staging.quince.com")
  const firstDot = host.indexOf('.')
  if (firstDot === -1) return undefined

  const subdomain = host.substring(0, firstDot) // e.g. "quince-pm-cart-b2b-shop"
  const baseDomain = host.substring(firstDot + 1) // e.g. "preview-staging.quince.com"

  const shopSuffix = '-shop' // eslint-disable-line i18next/no-literal-string
  if (!subdomain.endsWith(shopSuffix)) return undefined

  // Strip -shop suffix → "quince-pm-cart-b2b"
  const withoutShop = subdomain.slice(0, -shopSuffix.length)

  // Extract branch name: everything after the first hyphen (tenant is the first segment)
  // e.g. "quince-pm-cart-b2b" → tenant="quince", branch="pm-cart-b2b"
  const firstHyphen = withoutShop.indexOf('-')
  if (firstHyphen === -1) return undefined

  const branchName = withoutShop.slice(firstHyphen + 1)
  if (!branchName) return undefined

  return `${branchName}.${baseDomain}`
}

/**
 * Returns the D2C auth domain for the current (or given) B2B self-serve hostname.
 * Falls back to the hostname extracted from NEXT_PUBLIC_SITE_URL.
 *
 * For feature preview branches, dynamically derives the D2C domain from the
 * B2B hostname pattern without needing a static mapping entry.
 */
export function getD2CAuthDomain(b2bHost?: string): string {
  const host = b2bHost ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  return getPreviewD2CDomain(host) ?? new URL(SITE_URL).hostname
}

/**
 * Returns true if the given hostname is a B2B self-serve preview domain.
 */
export function isB2BSelfServePreviewDomain(host?: string): boolean {
  const effectiveHost = host ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  return getPreviewD2CDomain(effectiveHost) !== undefined
}
